import { logResults } from '@lune/common/logger'
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material'
import { type SelectChangeEvent } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { DEFAULT_CONFIG, api } from '../../api/config'
import { unique } from '../../utils'

interface MultipleSelectChipProps {
  values?: string[]
  initialValues?: string[]
  onSelectTarget: (targets: string[]) => void
}

const NOBODY_LIST = ['nobody@lunenetworks.com']
export const noValues: string[] = []

const buildTargets = async (): Promise<string[]> =>
  await api.fetch
    .single(DEFAULT_CONFIG)
    .then(logResults('config/list'))
    .then((result) => result.realms)
    .then((result) => result.map((r) => r.targets))
    .then((result) => result.flat())
    .then((result) => [...result, ...NOBODY_LIST])
    .then((result) => unique(result))

export const MultipleSelectChip: React.FC<MultipleSelectChipProps> = ({
  values = noValues,
  initialValues = noValues,
  onSelectTarget
}) => {
  const [internalPersonName, setInternalPersonName] = useState<string[]>(() =>
    initialValues.length > 0 ? initialValues : noValues
  )
  const personName = values.length > 0 ? values : internalPersonName
  const [targets, setTargets] = useState<string[]>(values)

  useEffect(() => {
    if (values.length === 0) {
      void buildTargets().then((result) => {
        setTargets(result)
      })
    }
  }, [values])

  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const {
      target: { value }
    } = event
    const newValue = typeof value === 'string' ? value.split(',') : value
    setInternalPersonName(newValue)
    onSelectTarget(newValue)
  }

  return (
    <div>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel id="demo-multiple-chip-label">Recipients</InputLabel>
        <Select
          labelId="demo-multiple-chip-label"
          id="demo-multiple-chip"
          multiple
          value={personName}
          onChange={handleChange}
          input={<OutlinedInput id="select-multiple-chip" label="Recipients" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} />
              ))}
            </Box>
          )}
          disabled={values.length > 0}
        >
          {targets.map((name) => (
            <MenuItem
              key={name}
              value={name}
              style={{
                fontWeight: !personName.includes(name) ? 400 : 700
              }}
            >
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}
